import {
    Controls,
    ControlsItem,
    Typography,
    Switch,
    Divider,
    CrossIcon,
    Button,
    ListItem
} from '@genestack/ui';
import React, {ChangeEvent} from 'react';

import {UiPermission, UserIdentity, UserRole} from '../../../../typings/user';
import {useUpdateUserMutation} from '../../../hooks/manage-users/use-update-user-mutation';
import {useCurrentUserQuery} from '../../../hooks/user/use-current-user-query';
import {useSessionQuery} from '../../../hooks/user/use-session-query';
import {Preloader} from '../../../components/preloader/preloader';

import {ApiAccessSection} from '../../../components/api-access/api-access-section';
import {PermissionSwitcher} from './permission-switcher';
import styles from './user-settings.module.css';
import {getFormattedDate} from '../../../utils/format-date';

interface Props {
    user: UserIdentity;
    onClose: () => void;
}

const BLOCK_GAP = 3;

/** Component for settings of user */
export function UserSettings({user, onClose}: Props) {
    const currentUserQuery = useCurrentUserQuery();
    const updateUser = useUpdateUserMutation();
    const isCurrentUser = user.id === currentUserQuery.data?.id;

    const sessionQuery = useSessionQuery(user.id);

    const handleSwitchChange = () => {
        updateUser.mutate({
            id: user.id,
            active: !user.active
        });
    };

    const handlePermissionsChange = (permission: UiPermission, checked: boolean) => {
        const permissions = checked
            ? [...user.permissions, permission]
            : user.permissions.filter((p) => p !== permission);

        updateUser.mutate({id: user.id, permissions});
    };

    const handleRoleChange = (e: ChangeEvent<HTMLInputElement>) => {
        updateUser.mutate({
            id: user.id,
            role: e.target.value as UserRole
        });
    };

    return (
        <div className={styles.root}>
            <div className={styles.header}>
                <Button
                    ghost
                    className={styles.closeBtn}
                    data-qa="close-user-settings"
                    onClick={onClose}
                    title="Close"
                    icon={<CrossIcon />}
                />

                <Typography variant="title" intent="quiet">
                    {user.name}
                </Typography>
                <Typography intent="quiet">{user.email}</Typography>

                <Divider variant="transparent" />

                {sessionQuery.isLoading || sessionQuery.isError ? (
                    <Preloader
                        show
                        wrapAll="div"
                        wrapEach="div"
                        count={1}
                        className={styles.lastLoginPreloader}
                    />
                ) : (
                    <Typography>
                        Last login:{' '}
                        {sessionQuery.data
                            ? getFormattedDate({
                                  date: sessionQuery.data.authenticatedAt,
                                  showTime: true,
                                  writeIfTodayOrYesterday: true
                              })
                            : 'never logged in'}
                    </Typography>
                )}

                <ControlsItem>
                    <Controls gap={1}>
                        <ControlsItem>
                            <Typography as="span">Status:</Typography>
                        </ControlsItem>
                        <ControlsItem>
                            <Switch
                                onCheckedChange={handleSwitchChange}
                                checked={user.active}
                                disabled={isCurrentUser}
                            />
                        </ControlsItem>
                        <ControlsItem>
                            <Typography as="span">
                                {user.active ? 'Active' : 'Deactivated'}
                            </Typography>
                        </ControlsItem>
                    </Controls>
                </ControlsItem>
            </div>

            <div className={styles.body}>
                <Divider variant="transparent" gap={BLOCK_GAP} />

                <Typography variant="header" intent="quiet">
                    Assigned permissions
                </Typography>

                <Divider variant="transparent" gap={2} />

                {Object.values(UiPermission).map((permission) => (
                    <React.Fragment key={permission}>
                        <PermissionSwitcher
                            value={permission}
                            onCheckedChange={handlePermissionsChange}
                            checked={user.permissions.includes(permission)}
                            disabled={permission === UiPermission.manageUsers && isCurrentUser}
                        />

                        <Divider variant="transparent" />
                    </React.Fragment>
                ))}

                <Typography variant="section">Create and edit documents</Typography>

                <div className={styles.radioButtonsList}>
                    <ListItem
                        as="label"
                        interactive
                        prepend={
                            <input
                                type="radio"
                                name="user-editor-role-input"
                                value={UserRole.EXPLORER}
                                checked={user.role === UserRole.EXPLORER}
                                onChange={handleRoleChange}
                            />
                        }
                    >
                        None (explorer-only access)
                    </ListItem>

                    <ListItem
                        as="label"
                        interactive
                        prepend={
                            <input
                                type="radio"
                                name="user-editor-role-input"
                                value={UserRole.BTB_EDITOR}
                                checked={user.role === UserRole.BTB_EDITOR}
                                onChange={handleRoleChange}
                            />
                        }
                    >
                        BTB-documents only
                    </ListItem>

                    <ListItem
                        as="label"
                        interactive
                        prepend={
                            <input
                                type="radio"
                                name="user-editor-role-input"
                                value={UserRole.EDITOR}
                                checked={user.role === UserRole.EDITOR}
                                onChange={handleRoleChange}
                            />
                        }
                    >
                        BTB-documents, assets and libraries
                    </ListItem>
                </div>

                <ApiAccessSection userId={user.id} />
            </div>
        </div>
    );
}
